import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { Link } from 'react-router-dom';
import { adminApi, type TermsChangeType, type TermsTrack } from '../services/api';

const MAX_CHARS = 50000;

type TabType = 'person' | 'company' | 'privacy' | 'refund' | 'delivery' | 'contract';
type AgreementField = 'personAgreement' | 'companyAgreement' | 'privacyPolicy' | 'refundPolicy' | 'deliveryInformation' | 'contractAgreement';

interface AgreementVersion {
  version: string;
  content: string;
  savedBy: string;
  savedByName: string;
  savedAt: string;
  /** Terms of service only. */
  title?: string;
  termsTrack?: TermsTrack;
  changeType?: TermsChangeType | null;
  sha256?: string;
}

type PublishedChangeType = Exclude<TermsChangeType, 'initial'>;

const isTermsField = (field: AgreementField) => field === 'personAgreement' || field === 'companyAgreement';
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'link'
];

export function AdminAgreements() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('person');
  const [personAgreement, setPersonAgreement] = useState('');
  const [companyAgreement, setCompanyAgreement] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [refundPolicy, setRefundPolicy] = useState('');
  const [deliveryInformation, setDeliveryInformation] = useState('');
  const [contractAgreement, setContractAgreement] = useState('');
  const [personVersions, setPersonVersions] = useState<AgreementVersion[]>([]);
  const [companyVersions, setCompanyVersions] = useState<AgreementVersion[]>([]);
  const [privacyVersions, setPrivacyVersions] = useState<AgreementVersion[]>([]);
  const [refundVersions, setRefundVersions] = useState<AgreementVersion[]>([]);
  const [deliveryVersions, setDeliveryVersions] = useState<AgreementVersion[]>([]);
  const [contractVersions, setContractVersions] = useState<AgreementVersion[]>([]);
  // Name, pilot/commercial and change type of the next terms version.
  const [termsTitle, setTermsTitle] = useState('');
  const [termsTrack, setTermsTrack] = useState<TermsTrack>('pilot');
  const [termsChangeType, setTermsChangeType] = useState<PublishedChangeType>('material');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadAgreementInfo();
  }, []);

  const loadAgreementInfo = async () => {
    try {
      const data = await adminApi.getAgreementInfo();
      setPersonAgreement(data.personAgreement?.content || '');
      setCompanyAgreement(data.companyAgreement?.content || '');
      setPrivacyPolicy(data.privacyPolicy?.content || '');
      setRefundPolicy(data.refundPolicy?.content || '');
      setDeliveryInformation(data.deliveryInformation?.content || '');
      setContractAgreement(data.contractAgreement?.content || '');
      setPersonVersions(data.personAgreement?.versions || []);
      setCompanyVersions(data.companyAgreement?.versions || []);
      setPrivacyVersions(data.privacyPolicy?.versions || []);
      setRefundVersions(data.refundPolicy?.versions || []);
      setDeliveryVersions(data.deliveryInformation?.versions || []);
      setContractVersions(data.contractAgreement?.versions || []);
      const termsVersions: AgreementVersion[] = data.personAgreement?.versions || [];
      const latestTerms = termsVersions[termsVersions.length - 1];
      setTermsTitle(latestTerms?.title ?? '');
      setTermsTrack(latestTerms?.termsTrack ?? 'pilot');
      setTermsChangeType('material');
    } catch (error) {
      console.error('Failed to load agreement info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlainTextLength = (html: string): number => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return (temp.textContent || temp.innerText || '').length;
  };

  const handleSave = async (field: AgreementField) => {
    const content = field === 'personAgreement'
      ? personAgreement
      : field === 'companyAgreement'
        ? companyAgreement
        : field === 'privacyPolicy'
          ? privacyPolicy
          : field === 'refundPolicy'
            ? refundPolicy
            : field === 'deliveryInformation'
              ? deliveryInformation
          : contractAgreement;

    if (getPlainTextLength(content) > MAX_CHARS) {
      setSaveMessage({ type: 'error', text: t('adminAgreements.maxCharsError', { max: MAX_CHARS.toLocaleString('sv-SE') }) });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateAgreementInfo(
        isTermsField(field)
          ? { field, content, title: termsTitle.trim() || undefined, termsTrack, changeType: termsChangeType }
          : { field, content }
      );
      // Reload data to get updated versions
      await loadAgreementInfo();
      setSaveMessage({ type: 'success', text: t('admin.agreements.saveSuccess') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save agreement:', error);
      setSaveMessage({ type: 'error', text: t('admin.agreements.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderVersionInfo = (versions: AgreementVersion[]) => {
    if (versions.length === 0) {
      return (
        <small className="text-muted">
          {t('admin.agreements.noVersions')}
        </small>
      );
    }
    const latest = versions[versions.length - 1];
    return (
      <small className="text-muted">
        {t('admin.agreements.version')} {latest.version} | {t('admin.agreements.lastSavedBy')} {latest.savedByName} | {formatDate(latest.savedAt)}
      </small>
    );
  };

  const renderTermsVersionFields = () => (
    <div className="row g-3 mb-3" data-testid="terms-version-fields">
      <div className="col-12 col-md-5">
        <label className="form-label" htmlFor="termsVersionTitle">{t('termsVersions.admin.title')}</label>
        <input
          id="termsVersionTitle"
          className="form-control"
          value={termsTitle}
          maxLength={160}
          placeholder={t(termsTrack === 'pilot' ? 'termsVersions.admin.titlePlaceholderPilot' : 'termsVersions.admin.titlePlaceholderCommercial')}
          onChange={(event) => setTermsTitle(event.target.value)}
        />
      </div>
      <div className="col-12 col-sm-6 col-md-3">
        <label className="form-label" htmlFor="termsVersionTrack">{t('termsVersions.admin.track')}</label>
        <select
          id="termsVersionTrack"
          className="form-select"
          value={termsTrack}
          onChange={(event) => {
            // A new track gets its own default name unless the admin types one.
            setTermsTrack(event.target.value as TermsTrack);
            setTermsTitle('');
          }}
        >
          <option value="pilot">{t('termsVersions.track.pilot')}</option>
          <option value="commercial">{t('termsVersions.track.commercial')}</option>
        </select>
      </div>
      <div className="col-12 col-sm-6 col-md-4">
        <label className="form-label" htmlFor="termsVersionChangeType">{t('termsVersions.admin.changeType')}</label>
        <select
          id="termsVersionChangeType"
          className="form-select"
          value={termsChangeType}
          onChange={(event) => setTermsChangeType(event.target.value as PublishedChangeType)}
        >
          <option value="material">{t('termsVersions.changeType.material')}</option>
          <option value="informational">{t('termsVersions.changeType.informational')}</option>
        </select>
      </div>
      <div className="col-12">
        <small className="text-muted">{t('termsVersions.admin.publishHelp')}</small>
      </div>
    </div>
  );

  const renderTermsVersionHistory = (versions: AgreementVersion[]) => (
    <div className="mt-4" data-testid="terms-version-history">
      <h6 className="fw-semibold mb-2">{t('termsVersions.admin.historyTitle')}</h6>
      {versions.length === 0 ? (
        <small className="text-muted">{t('admin.agreements.noVersions')}</small>
      ) : (
        <ul className="list-unstyled mb-0 terms-acceptances__list">
          {[...versions].reverse().map((version) => (
            <li key={version.version} className="terms-acceptances__item">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Link
                  to={`/anvandarvillkor?version=${encodeURIComponent(version.version)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-semibold text-break"
                >
                  {version.title ?? t('admin.agreements.version')} · {t('termsVersions.versionLabel', { version: version.version })}
                </Link>
                {version.termsTrack && (
                  <span className="badge bg-soft-primary text-primary">{t(`termsVersions.track.${version.termsTrack}`)}</span>
                )}
                {version.changeType && (
                  <span className="badge bg-soft-secondary text-secondary">{t(`termsVersions.changeType.${version.changeType}`)}</span>
                )}
              </div>
              <div className="small text-muted mt-1">
                {t('admin.agreements.lastSavedBy')} {version.savedByName} · {formatDate(version.savedAt)}
              </div>
              {version.sha256 && (
                <div className="small text-muted text-break terms-acceptances__checksum">
                  {t('termsVersions.checksum')}: <code>{version.sha256}</code>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderEditor = (
    value: string,
    onChange: (value: string) => void,
    helpText: string,
    versions: AgreementVersion[],
    field: AgreementField
  ) => (
    <Card>
      <div className="card-body">
        <p className="text-muted small mb-3">{helpText}</p>
        {isTermsField(field) && renderTermsVersionFields()}
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={quillModules}
          formats={quillFormats}
          style={{ minHeight: '200px' }}
        />
        <div className="d-flex justify-content-between align-items-center mt-2">
          <div>{renderVersionInfo(versions)}</div>
          <small className={getPlainTextLength(value) > MAX_CHARS ? 'text-danger' : 'text-muted'}>
            {getPlainTextLength(value)} / {MAX_CHARS} {t('common.characters') || 'tecken'}
          </small>
        </div>

        {/* Spacer for Quill toolbar */}
        <div style={{ height: '45px' }}></div>

        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-primary"
            onClick={() => handleSave(field)}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('common.saving') || 'Sparar...'}
              </>
            ) : (
              <>
                <i className="feather-save me-2"></i>
                {t('common.save')}
              </>
            )}
          </button>
        </div>
        {isTermsField(field) && renderTermsVersionHistory(versions)}
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('admin.agreements.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('admin.agreements.title') }
          ]} />
        </div>
      </div>

      {saveMessage && (
        <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
          {saveMessage.text}
          <button type="button" className="btn-close" onClick={() => setSaveMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="nav-tabs-wrapper">
          <ul className="nav nav-tabs nav-tabs-custom-style" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'person' ? 'active' : ''}`}
                onClick={() => setActiveTab('person')}
              >
                <i className="feather-user d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.person')}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'privacy' ? 'active' : ''}`}
                onClick={() => setActiveTab('privacy')}
              >
                <i className="feather-shield d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.privacy')}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'refund' ? 'active' : ''}`}
                onClick={() => setActiveTab('refund')}
              >
                <i className="feather-rotate-ccw d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.refund')}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'delivery' ? 'active' : ''}`}
                onClick={() => setActiveTab('delivery')}
              >
                <i className="feather-truck d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.delivery')}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'company' ? 'active' : ''}`}
                onClick={() => setActiveTab('company')}
              >
                <i className="feather-briefcase d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.company')}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'contract' ? 'active' : ''}`}
                onClick={() => setActiveTab('contract')}
              >
                <i className="feather-file d-inline d-md-none"></i>
                <span className="d-none d-md-inline">{t('admin.agreements.tabs.contract')}</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        <div className={`tab-pane fade ${activeTab === 'person' ? 'show active' : ''}`}>
          {renderEditor(
            personAgreement,
            setPersonAgreement,
            t('admin.agreements.personHelp'),
            personVersions,
            'personAgreement'
          )}
        </div>

        <div className={`tab-pane fade ${activeTab === 'company' ? 'show active' : ''}`}>
          {renderEditor(
            companyAgreement,
            setCompanyAgreement,
            t('admin.agreements.companyHelp'),
            companyVersions,
            'companyAgreement'
          )}
        </div>

        <div className={`tab-pane fade ${activeTab === 'contract' ? 'show active' : ''}`}>
          {renderEditor(
            contractAgreement,
            setContractAgreement,
            t('admin.agreements.contractHelp'),
            contractVersions,
            'contractAgreement'
          )}
        </div>

        <div className={`tab-pane fade ${activeTab === 'privacy' ? 'show active' : ''}`}>
          {renderEditor(
            privacyPolicy,
            setPrivacyPolicy,
            t('admin.agreements.privacyHelp'),
            privacyVersions,
            'privacyPolicy'
          )}
        </div>

        <div className={`tab-pane fade ${activeTab === 'refund' ? 'show active' : ''}`}>
          {renderEditor(
            refundPolicy,
            setRefundPolicy,
            t('admin.agreements.refundHelp'),
            refundVersions,
            'refundPolicy'
          )}
        </div>

        <div className={`tab-pane fade ${activeTab === 'delivery' ? 'show active' : ''}`}>
          {renderEditor(
            deliveryInformation,
            setDeliveryInformation,
            t('admin.agreements.deliveryHelp'),
            deliveryVersions,
            'deliveryInformation'
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
